/* eslint-disable lingui/no-unlocalized-strings -- caminhos e rótulos técnicos de log, não texto de UI */
import type { z } from 'zod';

import { billingApi } from '@/shared/http';

import {
  communicationSettingListQueryParams,
  communicationSettingOptionPageSchema,
  communicationSettingPageSchema,
  communicationSettingSchema,
  type CommunicationSettingCreateBody,
  type CommunicationSettingListParams,
  type CommunicationSettingUpdateBody,
} from './communication-setting.wire';

// Uma função por chamada, citando `UYZY@master` (`services/walletManagement/communicationSetting/index.ts`).
// BillingApi, timeout padrão (16 s), `Authorization: Bearer` pelo interceptor e nenhum `tenant`.

/** U27: fio fora do contrato só avisa fora de produção e segue com o valor cru. */
function wire<S extends z.ZodType>(schema: S, data: unknown, label: string): z.output<S> {
  const parsed = schema.safeParse(data);
  if (parsed.success) return parsed.data;
  if (!import.meta.env.PROD) console.warn(`${label} fora do contrato`, parsed.error.issues);
  return data as z.output<S>;
}

const withSignal = (signal: AbortSignal | undefined) => (signal ? { signal } : {});
const byId = (id: string) => `/CommunicationSetting/${encodeURIComponent(id)}`;

/** #1 `GET /CommunicationSetting` — `index.ts:6-17`, filtros de `communicationSettingContext.tsx:40-46`. */
export async function listCommunicationSettings(params: CommunicationSettingListParams, signal?: AbortSignal) {
  const data = await billingApi.get<unknown>('/CommunicationSetting', { params: communicationSettingListQueryParams(params), ...withSignal(signal) });
  return wire(communicationSettingPageSchema, data, 'GET /CommunicationSetting');
}

/** #2 `GET /CommunicationSetting/{id}` — `index.ts:19-25`. A tela não usa (edita a linha); abre o sheet depois de recarregar. */
export async function getCommunicationSetting(id: string, signal?: AbortSignal) {
  const data = await billingApi.get<unknown>(byId(id), withSignal(signal));
  return wire(communicationSettingSchema, data, 'GET /CommunicationSetting/{id}');
}

/** #3 `POST /CommunicationSetting` — `index.ts:35-40`, corpo de `communicationSettingContext.tsx:190-200`. */
export function createCommunicationSetting(body: CommunicationSettingCreateBody) {
  return billingApi.post('/CommunicationSetting', body);
}

/** #4 `PATCH /CommunicationSetting/{id}` — `index.ts:27-33`, corpo de `communicationSettingContext.tsx:152-162`. */
export function updateCommunicationSetting(id: string, body: CommunicationSettingUpdateBody) {
  return billingApi.patch<unknown>(byId(id), body);
}

/** #5 `DELETE /CommunicationSetting/{id}` — `index.ts:42-47`. */
export function deleteCommunicationSetting(id: string) {
  return billingApi.delete<unknown>(byId(id));
}

/**
 * Autocomplete de régua de comunicação para outros módulos (Cessionário, Parcelamento): o mesmo
 * `GET /CommunicationSetting` com `{ page: 0, size: 10, searchString, walletsCode }`
 * (`communicationSettingContext.tsx:46`, `:91-96`; hoje recriado em `fund.api.ts` #20).
 */
export async function listCommunicationSettingOptions(options: { walletsCode?: string | number | null | undefined; searchString?: string | undefined }, signal?: AbortSignal) {
  const params = { page: 0, size: 10, searchString: options.searchString, walletsCode: options.walletsCode ?? undefined };
  const data = await billingApi.get<unknown>('/CommunicationSetting', { params, ...withSignal(signal) });
  return wire(communicationSettingOptionPageSchema, data, 'GET /CommunicationSetting (opções)');
}
